import type { ComponentType } from 'react';
import { CLASSICAL } from '../materials';
import { levelEntry, type ModelProps } from '../parts';
import { Future, Industrial, Modern } from './eras';
import {
  Gatehouse,
  HOUSE_ANGLES,
  Houses,
  Keep,
  MORE_HOUSE_ANGLES,
  TowerBanners,
  Wall,
} from './parts';

/** L1 Settlement: the hex wall, the keep and five plaster houses. */
function Settlement({ accent }: ModelProps) {
  return (
    <>
      <Wall />
      <Keep accent={accent} />
      <Houses angles={HOUSE_ANGLES} />
    </>
  );
}

/** L2 Town: taller, capped corner towers and two more houses. */
function Town({ accent }: ModelProps) {
  return (
    <>
      <Wall tall />
      <Keep accent={accent} />
      <Houses angles={MORE_HOUSE_ANGLES} />
    </>
  );
}

/** L3 Stronghold: adds a gatehouse on the front wall and banners on the towers. */
function Stronghold({ accent }: ModelProps) {
  return (
    <>
      <Wall tall />
      <TowerBanners accent={accent} />
      <Gatehouse accent={accent} />
      <Keep accent={accent} />
      <Houses angles={MORE_HOUSE_ANGLES} />
    </>
  );
}

/** L4 Citadel: a bigger, two-tier keep and stone houses in place of plaster. */
function Citadel({ accent }: ModelProps) {
  return (
    <>
      <Wall tall />
      <TowerBanners accent={accent} />
      <Gatehouse accent={accent} />
      <Keep accent={accent} big />
      <Houses angles={MORE_HOUSE_ANGLES} material={CLASSICAL.STONE} />
    </>
  );
}

/**
 * City look by level: entry i is level i + 1. Levels past the end use the last
 * entry, so a new level's city is one more entry here. The later eras'
 * stages (L5 Industrial, L6 Modern, L7 Future) live in `eras.tsx`.
 */
const CITY_STAGES: readonly ComponentType<ModelProps>[] = [
  Settlement,
  Town,
  Stronghold,
  Citadel,
  Industrial,
  Modern,
  Future,
];

export function cityStage(level: number): ComponentType<ModelProps> {
  return levelEntry(CITY_STAGES, level);
}
